/**
 * src/features/referral/ui/PostExportPrompt.tsx
 *
 * One-time share prompt shown after the user's first export, at peak-value moment.
 * Mounts in App.tsx parallel to CommandMenu (global overlay level).
 *
 * Guard logic (RESEARCH Pattern 7, REFUI-05):
 *  - Condition: hasExported===true AND localStorage flag absent
 *  - Prompt is shown reactively: a Zustand store subscription triggers the show
 *    callback when hasExported transitions false→true AFTER mount. The callback
 *    runs outside the render cycle so setState is called from a subscription
 *    handler, not synchronously inside a useEffect body.
 *  - Flag write is guarded by a flagWritten ref so it executes exactly once.
 *  - Dismiss: sets local dismissed state → renders null; flag persists.
 *  - hasExported is intentionally session-only in useUIStore (NOT persisted).
 *    The localStorage flag is the cross-session "never show again" guard.
 *
 * T-27-07: localStorage flag tampering only affects this presentational prompt;
 *   no security impact.
 */

import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { useUIStore } from '@/stores/useUIStore';

/** localStorage key used as the permanent "already prompted" guard. */
const REFERRAL_PROMPTED_KEY = 'estimatix-referral-prompted';

export const PostExportPrompt: FC = () => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Guard: write the localStorage flag exactly once when the prompt first becomes visible.
  const flagWritten = useRef(false);

  useEffect(() => {
    // Subscribe to the store so we react when hasExported transitions false→true.
    // setState is called from the subscription callback (not synchronously in the
    // effect body) — satisfying react-hooks/set-state-in-effect.
    const tryShow = (hasExported: boolean) => {
      if (!hasExported) return;
      if (localStorage.getItem(REFERRAL_PROMPTED_KEY) !== null) return;
      if (flagWritten.current) return;
      flagWritten.current = true;
      localStorage.setItem(REFERRAL_PROMPTED_KEY, '1');
      setVisible(true);
    };

    // Run immediately for the current value (handles the case where hasExported
    // was already true at mount, e.g. during unit tests).
    tryShow(useUIStore.getState().hasExported);

    // Subscribe for future changes.
    const unsubscribe = useUIStore.subscribe((state) => tryShow(state.hasExported));
    return unsubscribe;
  }, []);

  if (!visible || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          role="complementary"
          aria-labelledby="post-export-prompt-heading"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: reduced ? 0 : 0.3 }}
          className="fixed bottom-6 right-4 z-50 w-80 max-w-[calc(100vw-32px)] bg-white dark:bg-slate-900 rounded-2xl shadow-card-hover border border-slate-100 dark:border-slate-800 p-4 sm:right-4 sm:bottom-6"
        >
          <div className="flex items-start justify-between gap-3">
            <Gift
              size={20}
              className="text-brand-600 dark:text-brand-400 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="flex-1 min-w-0">
              <p
                id="post-export-prompt-heading"
                className="text-body font-bold text-slate-900 dark:text-slate-100"
              >
                {t('referral.prompt_heading', 'Great work — share the tip!')}
              </p>
              <p className="text-body text-slate-600 dark:text-slate-400 mt-1">
                {t(
                  'referral.prompt_body',
                  'Know another contractor? Invite them and earn a free month of Pro for each paid subscription.',
                )}
              </p>
              <p className="mt-3 text-body font-bold text-brand-600 dark:text-brand-400">
                {t('referral.prompt_cta', 'Invite a colleague')}
              </p>
            </div>
            <button
              onClick={() => setDismissed(true)}
              aria-label={t('referral.prompt_dismiss_aria', 'Dismiss referral prompt')}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
